import { SxProps, Theme } from '@mui/material/styles';
import { aiGradientTextSx } from 'common/constants/ai';
import { AI_CURSOR, getAIFindingBoxSx } from './AIFindingValueViewer.styles';

// Reuse the AI finding cell's box so the merge viewer shares ONE source of truth for
// alignment + padding with every other value viewer. At rest the child IS the cell's
// own value viewer (which fills the cell and carries its `.text-cell` insets), so the
// box stays bare. Only while the merge is shown (renderMerge) do we render a bespoke
// "current -> ai" layout that does NOT pick up those insets: the box is absolutely
// positioned over the whole cell (left/right: 0), covering the td's `padding: 0 4px`,
// and the merge text has no `.text-cell { padding-right: 20px }` - so without this the
// AI value hugs the cell border, ~24px right of every normal right-aligned value. Mirror
// the cell's insets (4px td padding + 20px text-cell padding) ONLY in the merge state so
// the AI value lines up with the resting value, and add the AI cursor since the merge
// cell is interactive even without ctrl held. See _cell.scss. (The purple review outline
// is drawn as an outset ring on the gridline, so it no longer eats into this inset.)
export const getMergeBoxSx = (renderMerge: boolean, ctrlHeld = false): SxProps<Theme> => ({
  ...(getAIFindingBoxSx(ctrlHeld) as object),
  ...(renderMerge && {
    pl: '4px',
    pr: '20px',
    cursor: AI_CURSOR,
    '& *': { cursor: `${AI_CURSOR} !important` },
  }),
});

// A ref-forwarding anchor for the rest-state reveal Tooltip. MUI Tooltip needs a child
// that forwards a ref and accepts mouse events; the cell's own value viewer may do
// neither, so we wrap it in this span. The span is a transparent flex pass-through that
// mirrors getAIFindingBoxSx's layout rules exactly (the box sets `& > *` width/textAlign
// on its direct child, which is now this anchor - so we re-apply the same rules to OUR
// child viewer). Net: the viewer fills and vertically centers identically to bare rest
// content, so the cell's single-sourced alignment/padding is unchanged.
export const restRevealAnchorSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  minWidth: 0,
  overflow: 'hidden',
  '& > *': { width: '100%', textAlign: 'inherit' },
};

// Pending-review text tint: the AI gradient clipped to each text span itself, since a
// gradient on a wrapper goes invisible inside positioned subtrees (CitationValueViewer
// mounts its value in an absolutely-positioned overlay). Only the innermost text span
// carries it: a `.value-viewer` wrapping a `.text-cell` is skipped, otherwise the clipped
// gradient is painted twice and the text turns blurry. Date cells have neither class, so
// GridDateValueViewer's bare span is listed explicitly.
export const reviewPendingTextSx: SxProps<Theme> = {
  '& .text-cell, & .value-viewer:not(:has(.text-cell)), & .GridDateValueViewer-valueViewer span': {
    ...(aiGradientTextSx as object),
  },
};

// Default-state AI-purple anchor for a pending AI-suggested cell while the review
// drawer is open (SCAL-9150): the transparent layout pass-through plus the tint.
export const restPurpleAnchorSx: SxProps<Theme> = {
  ...(restRevealAnchorSx as object),
  ...(reviewPendingTextSx as object),
};

export const mergeTypographySx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  // gap: '2px',
  width: '100%',
  fontSize: 'inherit',
  lineHeight: 'inherit',
  minWidth: 0,
};

export const mergeCurrentValueSx: SxProps<Theme> = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  // The "before" value yields first in a narrow cell: it out-shrinks the AI value
  // (flexShrink 1) so the AI suggestion stays readable and only the old value
  // ellipsizes. The AI value still truncates as a last resort in extreme widths.
  flexShrink: 5,
  minWidth: 0,
};

export const mergeArrowSx: SxProps<Theme> = { mx: '1px', flexShrink: 2, fontSize: '9px' };

export const mergeAIValueSx: SxProps<Theme> = {
  fontWeight: 500,
  overflow: 'hidden',
  // textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flexGrow: 5,
  minWidth: 0,
};
